import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportApi } from '../api';
import type { ReportFormData, ReportListFilters } from '../types';

const GROUP_STATUS_MAP: Record<string, string> = {
  pending: 'DRAFT,SUBMITTED',
  approved: 'APPROVED',
  rejected: 'REJECTED',
};

const KEYS = {
  all: ['reports'] as const,
  lists: () => [...KEYS.all, 'list'] as const,
  list: (filters: ReportListFilters) => [...KEYS.lists(), filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  summary: (month?: string) => [...KEYS.all, 'summary', month] as const,
  myGroup: (group: string) => [...KEYS.all, 'my-group', group] as const,
  myGroupInfinite: (group: string) => [...KEYS.all, 'my-group', group, 'infinite'] as const,
};

export { KEYS as reportKeys };

export function useMyReports(filters: ReportListFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: async () => {
      const { data } = await reportApi.listMy(filters);
      return data;
    },
  });
}

export function useReportDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await reportApi.getById(id!);
      return data.report;
    },
    enabled: !!id,
  });
}

export function useReportSummary(month?: string) {
  return useQuery({
    queryKey: KEYS.summary(month),
    queryFn: async () => {
      const { data } = await reportApi.getSummary(month);
      return data.summary;
    },
  });
}

export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ReportFormData) => {
      const { data } = await reportApi.create(formData);
      return data.report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useUpdateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ReportFormData> }) => {
      const { data: response } = await reportApi.update(id, data);
      return response.report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await reportApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useMyReportsGroup(group: 'pending' | 'approved' | 'rejected') {
  const status = GROUP_STATUS_MAP[group];
  return useQuery({
    queryKey: KEYS.myGroup(group),
    queryFn: async () => {
      const { data } = await reportApi.listMy({ status, limit: 5, page: 1, sortBy: 'date', sortOrder: 'desc' });
      return { reports: data.reports, total: data.pagination.total };
    },
  });
}

export function useMyReportsByMonth(month: string) {
  const startDate = `${month}-01`;
  const endDate = `${month}-31`;
  return useQuery({
    queryKey: [...KEYS.all, 'my-month', month],
    queryFn: async () => {
      const { data } = await reportApi.listMy({
        startDate, endDate, limit: 100, page: 1, sortBy: 'date', sortOrder: 'desc',
      });
      return data.reports;
    },
  });
}

export function useMyReportsInfinite(group: 'pending' | 'approved' | 'rejected', pageSize = 10) {
  const status = GROUP_STATUS_MAP[group];
  return useInfiniteQuery({
    queryKey: KEYS.myGroupInfinite(group),
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await reportApi.listMy({ status, limit: pageSize, page: pageParam, sortBy: 'date', sortOrder: 'desc' });
      return { reports: data.reports, pagination: data.pagination };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
  });
}
